export class VariantSelector extends HTMLElement {
  private container: HTMLDivElement
  private select: HTMLSelectElement | null
  private dropdownButton: HTMLElement | null
  private sizeLabel: HTMLElement | null
  private options: NodeListOf<HTMLElement>
  private urlSync: boolean
  private pairFormId: string | null
  private priceSelector: string | null

  private static SELECTED_BUTTON_CLASSES = ['active']

  constructor() {
    super()
    // Create internal container element
    this.container = document.createElement('div')
    // Move all children to the container
    while (this.firstChild) {
      this.container.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to container
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.container.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.container)

    this.select = null
    this.dropdownButton = null
    this.sizeLabel = null
    this.urlSync = false
    this.pairFormId = null
    this.priceSelector = null
    this.options = this.container.querySelectorAll('[data-select-option]')
  }

  connectedCallback(): void {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize())
    } else {
      this.initialize()
    }
  }

  private initialize(): void {
    this.urlSync = this.hasAttribute('data-url-sync')
    this.pairFormId = this.getAttribute('data-pair-form-id')
    this.priceSelector = this.getAttribute('data-price-selector')
    this.select = this.parentNode ? this.parentNode.querySelector<HTMLSelectElement>('select[name="id"]') : null
    this.dropdownButton = this.container.querySelector<HTMLElement>('[data-modal-role="toggle"]')
    this.sizeLabel = this.dropdownButton?.querySelector<HTMLElement>('[size-selector-label') || null
    this.options = this.container.querySelectorAll<HTMLElement>('[data-select-option]')

    // Initialize stock indicators
    this.initializeStockIndicators()

    // Set up event listeners
    this.setupEventListeners()
  }

  private initializeStockIndicators(): void {
    const displayMode = this.getAttribute('data-stock-indicator-display')

    // Don't initialize stock indicators if display mode is 'none'
    if (displayMode === 'none') {
      return
    }

    // Initialize tooltips for all variants if display mode is 'tooltips'
    if (displayMode === 'tooltips') {
      this.options.forEach(option => {
        const variantId = option.dataset.variantId || ''
        const stockData = this.getVariantStockData(variantId)
        if (stockData) {
          const tooltipElement = this.container.querySelector(`[data-variant-stock-tooltip="${variantId}"]`)
          if (tooltipElement) {
            this.updateStockIndicatorContent(tooltipElement, stockData)
          }
        }
      })
    }

    // Initialize the "below" stock indicator with the selected variant
    if (displayMode === 'below') {
      const selectedOption = this.container.querySelector('[data-select-option].active')
      if (selectedOption) {
        this.updateStockIndicator(selectedOption as HTMLElement)
      }
    }
  }

  private updatePriceDisplay(option: HTMLElement): void {
    if (this.priceSelector) {
      const priceElement = document.getElementById(this.priceSelector)
      if (priceElement) {
        const priceCurrent = option.dataset.variantPrice || ''
        const priceCompare = option.dataset.variantPriceCompare || ''

        // Update CSS variables
        priceElement.style.setProperty('--price-current', `'${priceCurrent}'`)
        priceElement.style.setProperty('--price-compare', `'${priceCompare}'`)

        // Update data-sale attribute
        const onSale = priceCompare !== '' && priceCompare !== priceCurrent
        priceElement.setAttribute('data-sale', onSale.toString())
      }
    }
  }

  private updateStockIndicator(option: HTMLElement): void {
    const displayMode = this.getAttribute('data-stock-indicator-display')

    // Don't update stock indicators if display mode is 'none'
    if (displayMode === 'none') {
      return
    }

    const variantId = option.dataset.variantId || ''

    // Get pre-calculated stock data for this variant
    const stockData = this.getVariantStockData(variantId)
    if (!stockData) return

    // Update tooltip if display mode is 'tooltips'
    if (displayMode === 'tooltips') {
      const tooltipElement = this.container.querySelector(`[data-variant-stock-tooltip="${variantId}"]`)
      if (tooltipElement) {
        this.updateStockIndicatorContent(tooltipElement, stockData)
      }
    }

    // Update stock indicator below size selector if display mode is 'below'
    if (displayMode === 'below') {
      const stockIndicatorBelow = this.container.querySelector('[data-variant-stock-below]')
      if (stockIndicatorBelow) {
        this.updateStockIndicatorContent(stockIndicatorBelow, stockData)
      }
    }
  }

  private updateStockIndicatorContent(container: Element, stockData: {message: string, text_colour: string, background_colour: string}): void {
    const { message: stockMessage, text_colour: textColour, background_colour: backgroundColour } = stockData

    // Update the container content
    if (stockMessage) {
      let html = ''
      if (backgroundColour && backgroundColour !== ' ') {
        html += `<span style="background-color: ${backgroundColour}" class="px-0.5 py-0.375 text-xxs">`
      }
      html += `<span class="text-xxs" style="color: ${textColour};" aria-live="polite">${stockMessage}</span>`
      if (backgroundColour && backgroundColour !== ' ') {
        html += '</span>'
      }
      container.innerHTML = html
    } else {
      container.innerHTML = ''
    }
  }

  private getVariantStockData(variantId: string): {message: string, text_colour: string, background_colour: string} | null {
    // Get pre-calculated variant stock data from the variant selector element's data attribute
    const variantStockData = this.getAttribute('data-variant-stock-data')
    if (variantStockData) {
      try {
        const stockData = JSON.parse(variantStockData)
        return stockData[variantId] || null
      } catch (e) {
        console.warn('Failed to parse variant stock data:', e)
      }
    }

    return null
  }

  private getStockLevels(): Array<{max_quantity: number, message: string, message_colour: string, background_colour: string}> {
    // Get stock levels from the variant selector element's data attribute
    const stockLevelsData = this.getAttribute('data-stock-levels')
    if (stockLevelsData) {
      try {
        return JSON.parse(stockLevelsData)
      } catch (e) {
        console.warn('Failed to parse stock levels data:', e)
      }
    }

    // Fallback to empty array
    return []
  }

  private setupEventListeners(): void {
    // Handle option selection
    this.options.forEach(option => {
      option.addEventListener('click', (event) => this.handleOptionSelect(event))
      // Handle hover to update stock indicator - only on the button itself
      option.addEventListener('mouseenter', (event) => this.handleOptionHover(event))
      // Handle mouse leave on individual buttons to reset to selected variant
      option.addEventListener('mouseleave', () => this.resetStockIndicatorToSelected())
    })
  }

  private updatedSelectedState(option: HTMLElement): void {
    this.options.forEach(option => {
      option.classList.remove(...VariantSelector.SELECTED_BUTTON_CLASSES)
    })

    option.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
  }

  private handleOptionHover(event: Event): void {
    const option = event.currentTarget as HTMLElement
    // Update stock indicator on hover
    this.updateStockIndicator(option)
  }

  private resetStockIndicatorToSelected(): void {
    // Find the currently selected option
    const selectedOption = this.container.querySelector('[data-select-option].active')
    if (selectedOption) {
      this.updateStockIndicator(selectedOption as HTMLElement)
    } else {
      // Clear stock indicator if no variant is selected
      this.clearStockIndicator()
    }
  }

  private clearStockIndicator(): void {
    const displayMode = this.getAttribute('data-stock-indicator-display')

    // Don't clear stock indicators if display mode is 'none'
    if (displayMode === 'none') {
      return
    }

    // Clear stock indicator below size selector if display mode is 'below'
    if (displayMode === 'below') {
      const stockIndicatorBelow = this.container.querySelector('[data-variant-stock-below]')
      if (stockIndicatorBelow) {
        stockIndicatorBelow.innerHTML = ''
      }
    }
  }

  private handleOptionSelect(event: Event): void {
    const option = event.currentTarget as HTMLElement
    const variantId = option.dataset.variantId || ''
    const variantOption = option.dataset.variantOption || ''

    // Update the hidden select
    if (this.select) {
      this.select.value = variantId

      // Trigger change event for any listeners
      const changeEvent = new Event('change', { bubbles: true })
      this.select.dispatchEvent(changeEvent)
    }

    // Update price display
    this.updatePriceDisplay(option)

    // Update stock indicator
    this.updateStockIndicator(option)

    // Update the sticky pair form
    if (this.pairFormId) {
      const pairedElement = document.getElementById(this.pairFormId)
      if (pairedElement) {
        const hiddenSelector = pairedElement.querySelector('variant-selector')
        if (hiddenSelector) {
          const matchingOption = hiddenSelector.querySelector<HTMLElement>(`[data-variant-id="${variantId}"]`)
          if (matchingOption) {
            matchingOption.click()
          }
        }
      }
    }

    // Clear any other selected options
    this.updatedSelectedState(option)
    // Update the button text
    if (this.sizeLabel) {
      this.sizeLabel.textContent = variantOption

      if (this.dropdownButton) {
        this.dropdownButton.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
      }
    }

    if (this.urlSync) {
      // Update URL with variant
      window.history.replaceState({}, '', '?variant=' + variantId)
    }

    // Close the dropdown
    const closeButton = this.container.querySelector<HTMLElement>('[data-modal-role="close"]')
    if (closeButton) closeButton.click()
  }
}
